import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, ArrowRight, Share2 } from "lucide-react";
import { MarkdownRenderer } from "@/components/shared/MarkdownRenderer";
import { format } from "date-fns";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";

const ArticleDetails = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading } = useQuery({
    queryKey: ['article', slug],
    queryFn: async (): Promise<any> => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!slug
  });

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: (article as any)?.title,
        text: (article as any)?.excerpt || "",
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("הקישור הועתק ללוח");
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col justify-center items-center text-center">
          <h1 className="text-xl font-bold text-slate-900">המאמר לא נמצא</h1>
          <Button asChild variant="link" className="mt-4">
            <Link to="/articles">חזרה לבלוג</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const art = article as any;

  return (
    <Layout>
      <SEO 
        title={art.title}
        description={art.excerpt || ""}
        image={art.cover_image || ""}
        type="article"
      />

      <div className="bg-white min-h-screen pb-20">
        {/* Header */}
        <div className="pt-32 pb-10 border-b border-slate-100 bg-slate-50/30">
          <div className="container mx-auto px-6 max-w-3xl">
            <Link to="/articles" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-8 font-medium transition-colors">
              <ArrowRight className="h-4 w-4 ms-2" />
              חזרה למאמרים
            </Link>

            <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-6 leading-tight">
              {art.title}
            </h1>
            
            <div className="flex items-center justify-between text-sm text-slate-500 border-t border-slate-200/60 pt-6">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(art.created_at), 'dd/MM/yyyy')}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleShare} className="gap-2 text-slate-500 hover:text-blue-600">
                <Share2 className="w-4 h-4" />
                שתף
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 max-w-3xl py-12">
          {art.cover_image && (
            <div className="rounded-2xl overflow-hidden mb-12 shadow-sm border border-slate-100">
              <img 
                src={art.cover_image} 
                alt={art.title} 
                className="w-full h-auto object-cover max-h-[500px]"
              />
            </div>
          )}

          <div className="prose prose-slate prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-a:text-blue-600 prose-img:rounded-xl">
            <MarkdownRenderer content={art.content} allowStyle={true} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ArticleDetails;